// 語言檢測和切換工具
export type SupportedLanguage = 'en' | 'zh' | 'zh-TW' | 'zh-CN';

// 語言檢測配置
interface LanguageConfig {
  code: SupportedLanguage;
  name: string;
  nativeName: string;
  systemPrompt: string;
  responseLanguage: string;
}

// 支持的語言配置
const languageConfigs: Record<SupportedLanguage, LanguageConfig> = {
  'en': {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    systemPrompt: 'You are a professional insurance advisor. Please respond in English with a professional and friendly tone.',
    responseLanguage: 'English'
  },
  'zh': {
    code: 'zh',
    name: 'Chinese',
    nativeName: '中文',
    systemPrompt: '你是一位專業的保險顧問。請用中文以專業且友善的語調回答問題。',
    responseLanguage: '中文'
  },
  'zh-TW': {
    code: 'zh-TW',
    name: 'Traditional Chinese',
    nativeName: '繁體中文',
    systemPrompt: '你是一位專業的保險顧問。請用繁體中文以專業且友善的語調回答問題。',
    responseLanguage: '繁體中文'
  },
  'zh-CN': {
    code: 'zh-CN',
    name: 'Simplified Chinese',
    nativeName: '简体中文',
    systemPrompt: '你是一位专业的保险顾问。请用简体中文以专业且友善的语调回答问题。',
    responseLanguage: '简体中文'
  }
};


// 檢測文本語言
export const detectLanguage = (text: string): SupportedLanguage => {
  if (!text || text.trim().length === 0) {
    return 'en'; // 默認英文
  }

  const cleanText = text.toLowerCase().trim();
  
  // 檢查是否包含中文字符
  const hasChinese = /[\u4e00-\u9fff]/.test(text);
  
  if (hasChinese) {
    // 如果包含中文字符，進一步區分繁體和簡體
    const traditionalChars = ['繁體', '台灣', '香港', '澳門', '中華民國', '新台幣', '台幣', '們', '這', '個'];
    const simplifiedChars = ['简体', '中国', '大陆', '人民币', '元', '们', '这', '个'];
    
    const traditionalScore = traditionalChars.reduce((score, char) => 
      score + (cleanText.includes(char) ? 1 : 0), 0
    );
    const simplifiedScore = simplifiedChars.reduce((score, char) => 
      score + (cleanText.includes(char) ? 1 : 0), 0
    );

    if (traditionalScore > simplifiedScore) {
      return 'zh-TW';
    } else if (simplifiedScore > traditionalScore) {
      return 'zh-CN';
    } else {
      // 如果無法區分，默認使用繁體中文
      return 'zh-TW';
    }
  }

  // 如果沒有中文字符，檢查是否為純英文
  const hasEnglish = /[a-zA-Z]/.test(text);
  if (hasEnglish) {
    return 'en';
  }

  // 默認返回英文
  return 'en';
};

// 獲取語言配置
export const getLanguageConfig = (language: SupportedLanguage): LanguageConfig => {
  return languageConfigs[language] || languageConfigs['en'];
};

// 獲取所有支持的語言
export const getSupportedLanguages = (): LanguageConfig[] => {
  return Object.values(languageConfigs);
};

// 檢查是否為中文
export const isChinese = (language: SupportedLanguage): boolean => {
  return ['zh', 'zh-TW', 'zh-CN'].includes(language);
};

// 檢查是否為英文
export const isEnglish = (language: SupportedLanguage): boolean => {
  return language === 'en';
};

// 格式化語言名稱
export const formatLanguageName = (language: SupportedLanguage): string => {
  return getLanguageConfig(language).nativeName;
};

// 獲取系統提示詞
export const getSystemPrompt = (language: SupportedLanguage): string => {
  return getLanguageConfig(language).systemPrompt;
};

// 獲取回應語言說明
export const getResponseLanguage = (language: SupportedLanguage): string => {
  return getLanguageConfig(language).responseLanguage;
};

// 語言切換歷史管理
class LanguageHistory {
  private static instance: LanguageHistory;
  private history: Map<string, SupportedLanguage> = new Map();

  static getInstance(): LanguageHistory {
    if (!LanguageHistory.instance) {
      LanguageHistory.instance = new LanguageHistory();
    }
    return LanguageHistory.instance;
  }

  // 記錄用戶語言偏好
  recordUserLanguage(userId: string, language: SupportedLanguage) {
    this.history.set(userId, language);
  }

  // 獲取用戶語言偏好
  getUserLanguage(userId: string): SupportedLanguage | null {
    return this.history.get(userId) || null;
  }

  // 清除用戶語言歷史
  clearUserLanguage(userId: string) {
    this.history.delete(userId);
  }

  // 獲取所有語言歷史
  getAllHistory(): Map<string, SupportedLanguage> {
    return new Map(this.history);
  }
}

// 導出語言歷史管理器
export const languageHistory = LanguageHistory.getInstance();

// 便捷函數
export const detectAndGetLanguageConfig = (text: string): LanguageConfig => {
  const detectedLanguage = detectLanguage(text);
  return getLanguageConfig(detectedLanguage);
};

export const getLanguageFromText = (text: string): SupportedLanguage => {
  return detectLanguage(text);
};

const languageDetectionUtils = {
  detectLanguage,
  getLanguageConfig,
  getSupportedLanguages,
  isChinese,
  isEnglish,
  formatLanguageName,
  getSystemPrompt,
  getResponseLanguage,
  languageHistory,
  detectAndGetLanguageConfig,
  getLanguageFromText
};

export default languageDetectionUtils;
